const THREAD_CREATE_DRAFT_STORAGE_PREFIX = "nijiurachan-thread-create-draft:";

function getBrowserStorage(): Storage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

export function getThreadCreateDraftStorageKey(boardSlug: string): string {
  return `${THREAD_CREATE_DRAFT_STORAGE_PREFIX}${encodeURIComponent(boardSlug)}`;
}

export function isThreadCreateDraftStorageKey(
  key: string | null,
): key is string {
  return key?.startsWith(THREAD_CREATE_DRAFT_STORAGE_PREFIX) ?? false;
}

function removeStoredKey(
  boardSlug: string,
  storage: Storage | undefined,
): void {
  if (!storage) return;
  try {
    storage.removeItem(getThreadCreateDraftStorageKey(boardSlug));
  } catch {
    // Storage may be unavailable in private browsing or when its quota is full.
  }
}

/**
 * Sweep every persisted thread-create draft the current session can enumerate.
 *
 * スレッド本文はセッションを跨いで残さない方針(意図しない再表示は誤送信・情報漏えいの
 * 事故につながる)。読み込み・保存・破棄のいずれの経路でも同じ prefix を掃除し、
 * 旧バージョンで書き込まれた残留エントリも一括で消す。
 */
export function purgeAllThreadCreateDrafts(
  storage: Storage | undefined = getBrowserStorage(),
): void {
  if (!storage) return;
  const keysToRemove: string[] = [];
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (isThreadCreateDraftStorageKey(key)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) storage.removeItem(key);
  } catch {
    // 走査中に storage が失われる環境でも呼び出し側を止めない。
  }
}

/**
 * 本文は永続化しないため常に空文字を返す。呼び出し時に残留エントリがあれば
 * 併せて掃除する(旧クライアントで書き込まれた本文の再表示を防ぐため)。
 */
export function readThreadCreateDraft(
  boardSlug: string,
  storage: Storage | undefined = getBrowserStorage(),
): string {
  removeStoredKey(boardSlug, storage);
  purgeAllThreadCreateDrafts(storage);
  return "";
}

/**
 * 本文は永続化しない。誤って保存されていた残留エントリを掃除するだけに留める。
 */
export function saveThreadCreateDraft(
  boardSlug: string,
  _body: string,
  storage: Storage | undefined = getBrowserStorage(),
): void {
  removeStoredKey(boardSlug, storage);
}

export function clearThreadCreateDraft(
  boardSlug: string,
  storage: Storage | undefined = getBrowserStorage(),
): void {
  removeStoredKey(boardSlug, storage);
}

// モジュール読み込み時に一度だけ、既存の残留エントリを掃除する。
// 旧クライアントで localStorage に書き込まれたスレッド本文は、この時点で全て破棄する。
purgeAllThreadCreateDrafts();
